import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react"
import type * as React from "react"

import {
  menuContentClassName,
  menuItemPreviewHighlightClassName,
  menuItemStateClassName,
} from "@/components/ui/menu-item-styles"
import { cn } from "@/lib/utils"

const dropdownMenuContentClassName = `${menuContentClassName} max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) p-1`

const dropdownMenuItemClassName = `${menuItemStateClassName} relative flex h-8 cursor-default items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-3 text-small outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5`

const MENU_PREVIEW_HOVER = menuItemPreviewHighlightClassName
const MENU_PREVIEW_SELECTED = menuItemPreviewHighlightClassName

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  return (
    <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
  )
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  )
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  forceMount,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal forceMount={forceMount}>
      <DropdownMenuPrimitive.Content
        forceMount={forceMount}
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(dropdownMenuContentClassName, className)}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  )
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  variant?: "default" | "destructive"
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        dropdownMenuItemClassName,
        "data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(dropdownMenuItemClassName, "py-0 pr-3 pl-8", className)}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className="size-3.5" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return (
    <DropdownMenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(dropdownMenuItemClassName, "py-0 pr-3 pl-8", className)}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "text-muted-foreground text-mini px-3 py-1.5 font-medium data-[inset]:pl-8",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-muted-foreground text-mini ml-auto tracking-normal",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(dropdownMenuItemClassName, className)}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-3.5" />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(dropdownMenuContentClassName, className)}
      {...props}
    />
  )
}

function MenuItemPreviewCell({
  label,
  state,
}: {
  label: string
  state: "default" | "hover" | "selected" | "disabled"
}) {
  const stateClass =
    state === "hover"
      ? MENU_PREVIEW_HOVER
      : state === "selected"
        ? MENU_PREVIEW_SELECTED
        : undefined

  return (
    <div
      data-slot="dropdown-menu-item"
      data-preview-state={state === "default" ? undefined : state}
      className={cn(
        dropdownMenuItemClassName,
        stateClass,
        state === "disabled" && "pointer-events-none opacity-50",
      )}
      aria-label={`menu item ${label}`}
    >
      Inventory
      {state === "selected" ? <CheckIcon className="ml-auto size-3.5" /> : null}
    </div>
  )
}

function MenuPreviewRow() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-section-label text-muted-foreground w-24 shrink-0">
        Menu item
      </span>
      <MenuItemPreviewCell label="default" state="default" />
      <MenuItemPreviewCell label="hover" state="hover" />
      <MenuItemPreviewCell label="selected" state="selected" />
      <MenuItemPreviewCell label="disabled" state="disabled" />
    </div>
  )
}

function MenuPreview() {
  return (
    <div
      data-slot="menu-preview"
      className="border-border space-y-3 rounded-[var(--radius-control)] border-[0.5px] p-3"
    >
      <MenuPreviewRow />
      <div
        data-slot="dropdown-menu-content"
        data-preview-state="open"
        className={cn(dropdownMenuContentClassName, "relative w-52")}
      >
        <div
          data-slot="dropdown-menu-item"
          className={dropdownMenuItemClassName}
        >
          Inventory
        </div>
        <div
          data-slot="dropdown-menu-item"
          data-preview-state="hover"
          className={cn(dropdownMenuItemClassName, MENU_PREVIEW_HOVER)}
        >
          Pending sale
        </div>
        <div
          data-slot="dropdown-menu-item"
          data-preview-state="selected"
          className={cn(dropdownMenuItemClassName, MENU_PREVIEW_SELECTED)}
        >
          Sold
          <CheckIcon className="ml-auto size-3.5" />
        </div>
      </div>
    </div>
  )
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  MenuPreview,
  dropdownMenuContentClassName,
  dropdownMenuItemClassName,
}
